import React, { useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const TIER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'free', label: 'Free tier' },
  { value: 'paid', label: 'Paid only' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleString('en-GB'); } catch { return value; }
};

const difficultyBadge = (d) => {
  if (d === 'starter') return <Badge bg="success">Starter</Badge>;
  if (d === 'advanced') return <Badge bg="danger">Advanced</Badge>;
  return <Badge bg="info">Standard</Badge>;
};

const FacilitatorSpotErrorLibrary = ({
  drills = [],
  onCreate,
  onEdit,
  onPublish,
  onArchive,
  onDelete,
  onBack,
}) => {
  const [tierFilter, setTierFilter] = useState('all');
  const filtered = useMemo(() => {
    if (tierFilter === 'all') return drills;
    if (tierFilter === 'free') return drills.filter((d) => d.freeTier);
    return drills.filter((d) => !d.freeTier);
  }, [drills, tierFilter]);
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))),
    [filtered]
  );

  return (
    <div className="student-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title="Spot-the-Error library"
          description="Time-pressured chart-flagging exercises. Each one presents a brief patient + chart and asks the student to flag prescribing issues against the clock. Author your own or use the seeded library as a starting point."
          onBack={onBack}
          actions={<Button type="button" variant="primary" size="sm" onClick={onCreate}>New spot-the-error exercise</Button>}
        />

        <div className="d-flex gap-2 flex-wrap mb-3 align-items-center">
          <span className="small text-muted me-1">Tier:</span>
          {TIER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={tierFilter === option.value ? 'primary' : 'outline-secondary'}
              onClick={() => setTierFilter(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {sorted.length === 0 ? (
          <Alert variant="info">
            {tierFilter === 'all' ? (
              <>No spot-the-error exercises yet. Click <strong>New spot-the-error exercise</strong> to author one,
              or ask the platform admin to seed the curated library on this tenant.</>
            ) : (
              <>No exercises match this tier filter. <Button variant="link" className="p-0 align-baseline" onClick={() => setTierFilter('all')}>Show all</Button></>
            )}
          </Alert>
        ) : (
          <div className="table-responsive">
            <Table hover className="facilitator-library-table align-middle mb-0">
              <colgroup>
                <col className="facilitator-library-col--main" />
                <col className="facilitator-library-col--meta" />
                <col className="facilitator-library-col--actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Exercise</th>
                  <th>Updated / Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div className="fw-semibold">{d.title}</div>
                      <div className="small text-muted">{d.summary || 'No summary set'}</div>
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        {difficultyBadge(d.difficulty)}
                        <Badge bg="light" text="dark">{d.timeLimitSeconds || 90}s limit</Badge>
                        <Badge bg="light" text="dark">{d.expectedIssueCount || 0} issue{d.expectedIssueCount === 1 ? '' : 's'}</Badge>
                      </div>
                    </td>
                    <td>
                      <div className="small text-muted">{formatDate(d.updatedAt)}</div>
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        {d.status === 'published' ? <Badge bg="success">Published</Badge>
                          : d.status === 'archived' ? <Badge bg="secondary">Archived</Badge>
                          : <Badge bg="secondary">Draft</Badge>}
                        {d.freeTier ? <Badge bg="success">Free tier</Badge> : null}
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="d-flex flex-wrap gap-1 justify-content-end">
                        <Button size="sm" variant="outline-primary" onClick={() => onEdit?.(d)}>Edit</Button>
                        {d.status !== 'published'
                          ? <Button size="sm" variant="outline-success" onClick={() => onPublish?.(d.id)}>Publish</Button>
                          : null}
                        {d.status === 'published'
                          ? <Button size="sm" variant="outline-dark" onClick={() => onArchive?.(d.id)}>Archive</Button>
                          : null}
                        {d.status !== 'published'
                          ? <Button size="sm" variant="outline-danger" onClick={() => onDelete?.(d.id)}>Delete</Button>
                          : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Container>
    </div>
  );
};

export default FacilitatorSpotErrorLibrary;
